<template>
  <page-article>
    <div class="col-12 page_content_area">
      <campus-recruiting-introduction :basic-info="currentRecruitmentGetter" />
      <!-- 活動流程 -->
      <div class="page_article_block row">
        <div class="col-12">
          <div class="page_title">
            活動流程
          </div>
        </div>
        <div class="col-12 process_area justify-content-center clearfix">
          <stepper
            :steps="steps"
            :active-step="activeSegment"
            :enable-final-step="enableFinalStep"
            @active:updated="updateActiveSegment"
          />
        </div>
      </div>
      <div class="col-12">
        <portlet class="campus_recruiting_content_area page_article_block">
          <keep-alive>
            <component
              :is="activeSegmentComponent"
              ref="step"
            />
          </keep-alive>
          <div class="col-12  mt-5">
            <div class="row">
              <div class="col-sm-5 text-left">
                <button-wrapper
                  v-if="activeSegment !== '專刊資料確認'"
                  size="lg"
                  color="lighten_gray"
                  @click="prevStep"
                >
                  &larr; 上一步
                </button-wrapper>
              </div>
              <div
                v-if="!enableFinalStep"
                class="col-sm-7 text-right"
              >
                <template v-if="activeSegment !== '專刊資料確認'">
                  <button-wrapper
                    class="mb-sm-2"
                    color="pink"
                    size="lg"
                    @click="notSaveNextStep"
                  >
                    不購買前往下一步 &rarr;
                  </button-wrapper>
                  <span class="mr-2 ml-2">或</span>
                </template>
                <button-wrapper
                  size="lg"
                  @click="saveNextStep"
                >
                  儲存並前往下一步 &rarr;
                </button-wrapper>
              </div>
              <div
                v-else
                class="col-sm-7 text-right"
              >
                <button-wrapper
                  color="green"
                  size="lg"
                  :disabled="isSubmitting"
                  :is-loading="isSubmitting"
                  @click="submitEnrollment"
                >
                  送出報名表 &rarr;
                </button-wrapper>
              </div>
            </div>
          </div>
        </portlet>
      </div>
    </div>
  </page-article>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import pageArticle from '~components/pages/Article'
import Navigation from '~components/common/Navigation'
import Portlet from '~components/common/Portlet'
import Stepper from '~lar/Stepper'
import ButtonWrapper from '~components/common/buttons'

import CampusRecruitingIntroduction from './common/CampusRecruitingIntroduction'
import EnrollmentProcessSpecialIssue from './nested/EnrollmentProcessSpecialIssue'
import EnrollmentProcessRecruitmentOrientation from './nested/EnrollmentProcessRecruitmentOrientation'
import EnrollmentProcessCareerExpo from './nested/EnrollmentProcessCareerExpo'
import EnrollmentProcessPrintAdvertising from './nested/EnrollmentProcessPrintAdvertising'
import EnrollmentProcessOrderConfirmation from './nested/EnrollmentProcessOrderConfirmation'

const INACTIVE = 0
const COMPLETED = 1
const NOT_PURCHASED = 2
const DISABLED = 3
const ENROLL = 0
const PURCHASE = 1

const errorCodeRedirect = {
  461: '專刊資料確認',
  462: '徵才說明會'
}

const errorCodeReact = {
  462: 'updateRecruitmentOrientationList'
}

export default {
  name: 'CampusRecruitingEnrollmentProcess',
  components: {
    pageArticle,
    Navigation,
    Portlet,
    ButtonWrapper,
    CampusRecruitingIntroduction,
    EnrollmentProcessSpecialIssue,
    EnrollmentProcessRecruitmentOrientation,
    EnrollmentProcessCareerExpo,
    EnrollmentProcessPrintAdvertising,
    EnrollmentProcessOrderConfirmation,
    Stepper
  },
  data () {
    return {
      segments: ['專刊資料確認', '徵才說明會', '徵才博覽會', '商品項目購買', '送出報名表'],
      segmentComponents: [
        EnrollmentProcessSpecialIssue,
        EnrollmentProcessRecruitmentOrientation,
        EnrollmentProcessCareerExpo,
        EnrollmentProcessPrintAdvertising,
        EnrollmentProcessOrderConfirmation
      ],
      activeSegment: '專刊資料確認',
      enableFinalStep: false,
      steps: [{
        label: '專刊資料確認',
        stage: INACTIVE,
        action: ENROLL,
        isActive: true
      }, {
        label: '徵才說明會',
        stage: DISABLED,
        action: ENROLL,
        isActive: false
      }, {
        label: '徵才博覽會',
        stage: DISABLED,
        action: ENROLL,
        isActive: false
      }, {
        label: '商品項目購買',
        stage: DISABLED,
        action: PURCHASE,
        isActive: false
      }],
      status: [false, false, false],
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters('pages/campus-recruiting', [
      'currentRecruitmentGetter'
    ]),
    ...mapGetters('pages/enterprises', [
      'currentEnterpriseCheckoutGetter',
      'currentEnterpriseGetter'
    ]),
    activeSegmentComponent () {
      return this.segmentComponents[this.activeSegmentIndex]
    },
    activeSegmentIndex () {
      return this.segments.indexOf(this.activeSegment)
    },
    prevActiveStep () {
      const prevSteps = this.steps.slice(0, this.activeSegmentIndex || this.segments.length).reverse()
      return prevSteps.find(step => step.stage < DISABLED)
    },
    nextActiveStep () {
      const nextSteps = this.steps.slice(this.activeSegmentIndex + 1)
      return nextSteps.find(step => step.stage < DISABLED)
    }
  },
  watch: {
    currentRecruitmentGetter (value) {
      this.status = [value.recruitment_orientation_enabled, value.career_expo_enabled, value.print_advertising_enabled]
    }
  },
  async created () {
    this.togglePageLoader(true)
    this.clearOrders()
    try {
      await this.getRecruitmentDetailsById(this.$route.params.id)
    } catch (err) {
      this.$router.push('/campus-recruiting')
    }
    await this.getEnterpriseByToken()
    this.togglePageLoader(false)
  },
  beforeDestroy () {
    this.isSubmitting = false
  },
  methods: {
    ...mapActions('pages/campus-recruiting', [
      'getRecruitmentDetailsById',
      'getOrientationsDetailsById'
    ]),
    ...mapActions('pages/enterprises', [
      'getEnterpriseByToken',
      'removeOrder',
      'clearOrders',
      'submitCampusRecruitmentEnrollment',
      'getEnrolledCampusRecruitmentById'
    ]),
    ...mapActions('layouts', [
      'newToast',
      'togglePageLoader'
    ]),
    updateActiveSegment (segment) {
      this.activeSegment = segment
      window.scrollTo(0, 800)
      this.enableFinalStep = segment === '送出報名表'
    },
    async nextStep () {
      if (this.activeSegment === '專刊資料確認') {
        await this.activeEnrollment()
      }

      if (this.nextActiveStep) {
        await this.updateActiveSegment(this.nextActiveStep.label)
      } else {
        await this.checkPurchasedAny()
      }
      return true
    },
    checkPurchasedAny () {
      if (Object.keys(this.currentEnterpriseCheckoutGetter).length > 0) {
        this.updateActiveSegment('送出報名表')
      } else {
        this.newToast({
          variant: 'warning',
          message: '尚未購買任何東西'
          // speed: 'normal'
        })
      }
    },
    prevStep () {
      if (this.prevActiveStep) {
        this.updateActiveSegment(this.prevActiveStep.label)
        this.enableFinalStep = false
      }
    },
    notSaveNextStep () {
      switch (this.activeSegment) {
        case '徵才說明會':
          this.removeOrder('recruitmentOrientations')
          break
        case '徵才博覽會':
          this.removeOrder('careerExpo')
          break
        case '商品項目購買':
          this.removeOrder('printAdvertising')
          break
        default:
          break
      }
      this.$refs.step.reset()
      this.notPurchaseEnrollment()
      this.nextStep()
    },
    notPurchaseEnrollment () {
      this.steps.splice(this.activeSegmentIndex, 1, Object.assign({}, this.steps[this.activeSegmentIndex], {
        stage: NOT_PURCHASED
      }))
    },
    purchaseEnrollment () {
      this.steps.splice(this.activeSegmentIndex, 1, Object.assign({}, this.steps[this.activeSegmentIndex], {
        stage: COMPLETED
      }))
      this.$refs.step.saveToStore()
    },
    async activeEnrollment () {
      for (let i = 1; i < 4; i++) {
        await this.steps.splice(i, 1, Object.assign({}, this.steps[i], {
          stage: this.status[i - 1] ? INACTIVE : DISABLED
        }))
      }
      return true
    },
    checkValidations () {
      if (this.$refs.step) {
        return !this.$refs.step.$v.$invalid
      }
      return false
    },
    saveNextStep () {
      if (Object.keys(this.$refs.step).includes('$v')) {
        this.$refs.step.$v.$touch()
        if (this.checkValidations()) {
          this.purchaseEnrollment()
          this.nextStep()
        } else {
          this.newToast({
            variant: 'warning',
            message: '請確認購買數量'
          // speed: 'normal'
          })
        }
      } else {
        this.purchaseEnrollment()
        this.nextStep()
      }
    },
    async doubleCheck () {
      try {
        const { enterpriseId } = await this.getEnrolledCampusRecruitmentById(this.$route.params.id)
        return Number(enterpriseId) === Number(this.currentEnterpriseGetter.enterprise_id)
      } catch (err) {
        return false
      }
    },
    async submitEnrollment () {
      this.isSubmitting = true
      try {
        await this.submitCampusRecruitmentEnrollment({
          campus_recruitment_id: this.currentRecruitmentGetter.recruitment_id,
          ...this.currentEnterpriseCheckoutGetter
        })

        const isExist = await this.doubleCheck()
        if (isExist) {
          this.newToast({
            variant: 'success',
            message: '報名成功'
          })
          this.$router.replace({
            path: '/enterprises',
            query: {
              tab: '活動專區'
            }
          })
        } else {
          this.newToast({
            variant: 'danger',
            message: '報名失敗 - 系統忙碌中，請稍候再試'
          })
        }
      } catch (err) {
        const errorCode = err.response.data.status

        if (errorCode in errorCodeRedirect) {
          await this.updateActiveSegment(errorCodeRedirect[errorCode])
        }
        if (errorCode in errorCodeReact) {
          await this[errorCodeReact[errorCode]]()
        }
      }
      this.isSubmitting = false
    },
    async updateRecruitmentOrientationList () {
      this.togglePageLoader(true)
      await this.getOrientationsDetailsById(this.$route.params.id)
      this.togglePageLoader(false)
    }
  }

}
</script>
